import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Admin.css';
import API_BASE from '../config';
import { AuthContext } from '../context/AuthContext';
import { PAGE_LIST } from '../utils/pagePermissions';

const SYSTEM_ROLES = ['USER', 'ADMIN', 'COUNTRY_ADMIN', 'SUPERADMIN'];

const BLANK_CREATE = {
  firstName: '', lastName: '', email: '', username: '',
  employeeId: '', password: '', role: 'USER', departmentId: '', countryId: ''
};
const BLANK_EDIT = {
  firstName: '', lastName: '', email: '', username: '',
  employeeId: '', role: '', departmentId: '', countryId: ''
};

function UserManagement() {
  const { user: currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const isSuperAdmin = currentUser?.role === 'SUPERADMIN';

  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ departmentId: '' });
  const [showInactive, setShowInactive] = useState(false);

  const [createModal, setCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(BLANK_CREATE);
  const [createError, setCreateError] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState(BLANK_EDIT);
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  const [resetModal, setResetModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetError, setResetError] = useState('');

  // Page permission state (used inside edit modal)
  const [editPagePerms, setEditPagePerms] = useState(null); // null = loading, [] = all, [...] = restricted
  const [savingPerms, setSavingPerms] = useState(false);
  const [permsMsg, setPermsMsg] = useState('');



  useEffect(() => { fetchData(); }, [filters, showInactive]); // eslint-disable-line

  const token = () => localStorage.getItem('token');

  const fetchData = async () => {
    try {
      const inactive = showInactive ? '&showInactive=1' : '';
      const [usersRes, deptRes, countriesRes] = await Promise.all([
        fetch(`${API_BASE}/admin/users?departmentId=${filters.departmentId}${inactive}`, { headers: { Authorization: `Bearer ${token()}` } }),
        fetch(`${API_BASE}/admin/departments`, { headers: { Authorization: `Bearer ${token()}` } }),
        fetch(`${API_BASE}/admin/countries`, { headers: { Authorization: `Bearer ${token()}` } })
      ]);
      if (usersRes.ok) setUsers((await usersRes.json()).users || []);
      if (deptRes.ok) setDepartments((await deptRes.json()).departments || []);
      if (countriesRes.ok) setCountries((await countriesRes.json()).countries || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setCreateLoading(true); setCreateError('');
    try {
      const res = await fetch(`${API_BASE}/admin/users`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...createForm, departmentId: createForm.departmentId || undefined, countryId: createForm.countryId ? parseInt(createForm.countryId) : undefined })
      });
      const data = await res.json();
      if (!res.ok) { setCreateError(data.message); return; }
      setCreateModal(false); setCreateForm(BLANK_CREATE); fetchData();
    } catch (e) { setCreateError('Network error'); }
    finally { setCreateLoading(false); }
  };

  const openEdit = (user) => {
    setEditForm({ firstName: user.firstName, lastName: user.lastName, email: user.email || '', username: user.username, employeeId: user.employeeId, role: user.role || 'USER', departmentId: user.department?.id || '', countryId: user.country?.id || '' });
    setEditError(''); setEditModal(user);
    setEditPagePerms(null); setPermsMsg('');
    // Load existing page permissions
    fetch(`${API_BASE}/admin/users/${user.id}/page-permissions`, {
      headers: { Authorization: `Bearer ${token()}` }
    }).then(r => r.json()).then(d => setEditPagePerms(d.allowedPages || []))
      .catch(() => setEditPagePerms([]));
  };

  const handleEdit = async (e) => {
    e.preventDefault(); setEditLoading(true); setEditError('');
    try {
      const res = await fetch(`${API_BASE}/admin/users/${editModal.id}/full-edit`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...editForm, departmentId: editForm.departmentId || null, countryId: editForm.countryId ? parseInt(editForm.countryId) : null })
      });
      const data = await res.json();
      if (!res.ok) { setEditError(data.message); return; }
      setEditModal(null); fetchData();
    } catch (e) { setEditError('Network error'); }
    finally { setEditLoading(false); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 10) { setResetError('Password must be at least 10 characters'); return; }
    try {
      const res = await fetch(`${API_BASE}/admin/users/${resetModal.id}/reset-password`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPassword })
      });
      const data = await res.json();
      if (!res.ok) { setResetError(data.message); return; }
      setResetModal(null); setNewPassword(''); setResetError(''); alert('Password reset successfully');
    } catch (e) { setResetError('Network error'); }
  };





  const handleDeactivate = async (userId) => {
    if (!window.confirm('Deactivate this user?')) return;
    await fetch(`${API_BASE}/admin/users/${userId}/deactivate`, { method: 'PUT', headers: { Authorization: `Bearer ${token()}` } });
    fetchData();
  };

  const handleActivate = async (userId) => {
    await fetch(`${API_BASE}/admin/users/${userId}/activate`, { method: 'PUT', headers: { Authorization: `Bearer ${token()}` } });
    fetchData();
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Permanently delete ${user.firstName} ${user.lastName}? This cannot be undone.`)) return;
    const res = await fetch(`${API_BASE}/admin/users/${user.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } });
    if (res.ok) fetchData();
    else { const d = await res.json(); alert(d.message); }
  };

  const roleBadge = (role) => {
    const colors = { SUPERADMIN: '#7c3aed', ADMIN: '#dc2626', HR: '#d97706', IT: '#2563eb', USER: '#6b7280' };
    return <span style={{ background: colors[role] || '#6b7280', color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: '0.75rem', fontWeight: 600 }}>{role}</span>;
  };

  if (loading) return <div className="admin-container"><p>Loading users...</p></div>;

  return (
    <div className="admin-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate('/admin')} className="btn-back-nav">← Back</button>
          <h1 style={{ margin: 0 }}>User Management</h1>
        </div>
        {isSuperAdmin && (
          <button className="btn-primary" onClick={() => { setCreateForm(BLANK_CREATE); setCreateError(''); setCreateModal(true); }}>
            + Create User
          </button>
        )}
      </div>

      <div className="filters-section" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={filters.departmentId} onChange={(e) => setFilters({ ...filters, departmentId: e.target.value })} className="filter-select">
          <option value="">All Departments</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        {isSuperAdmin && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9rem' }}>
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            Show inactive users
          </label>
        )}
      </div>

      <p style={{ fontSize: '0.85rem', color: '#888', marginBottom: 8 }}>{users.length} user{users.length !== 1 ? 's' : ''}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
        {users.map(user => (
          <div key={user.id} style={{
            background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12,
            padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            display: 'flex', flexDirection: 'column', gap: 6,
            opacity: user.isActive ? 1 : 0.55,
            borderLeft: `4px solid ${user.isActive ? '#22c55e' : '#dc2626'}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <strong style={{ fontSize: 16, color: '#222' }}>{user.firstName} {user.lastName}</strong>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {roleBadge(user.role || 'USER')}
                <span style={{ color: user.isActive ? '#16a34a' : '#dc2626', fontWeight: 600, fontSize: '0.75rem' }}>
                  {user.isActive ? '● Active' : '● Inactive'}
                </span>
              </div>
            </div>
            <div style={{ fontSize: 13, color: '#555', lineHeight: 2 }}>
              <div><strong>Username:</strong> <code style={{ fontSize: '0.82rem', background: '#f3f4f6', padding: '1px 6px', borderRadius: 3 }}>{user.username}</code></div>
              <div><strong>Employee ID:</strong> <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{user.employeeId}</span></div>
              <div><strong>Email:</strong> {user.email || '—'}</div>
              <div><strong>Department:</strong> {user.department?.name || '—'}</div>
              <div><strong>Country:</strong> {user.country?.name || '—'}</div>
              <div><strong>Last Login:</strong> {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}</div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, borderTop: '1px solid #f0f0f0', paddingTop: 10, justifyContent: 'center' }}>
              {isSuperAdmin ? (
                <>
                  <button className="btn-small btn-primary" onClick={() => openEdit(user)}>Edit</button>
                  <button className="btn-small btn-secondary" onClick={() => { setResetModal(user); setNewPassword(''); setResetError(''); }}>Reset Password</button>
                  {user.isActive
                    ? <button className="btn-small btn-danger" onClick={() => handleDeactivate(user.id)}>Deactivate</button>
                    : <button className="btn-small" style={{ background: '#16a34a', color: '#fff' }} onClick={() => handleActivate(user.id)}>Activate</button>
                  }
                  <button className="btn-small btn-danger" onClick={() => handleDelete(user)}>Delete</button>
                </>
              ) : (
                <>
                  <button className="btn-small btn-primary" onClick={() => openEdit(user)}>Edit</button>
                  {user.isActive && <button className="btn-small btn-danger" onClick={() => handleDeactivate(user.id)}>Deactivate</button>}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* CREATE USER MODAL */}
      {createModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <h2>Create New User</h2>
            <form onSubmit={handleCreate}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div><label>First Name *</label><input className="input-full" value={createForm.firstName} onChange={e => setCreateForm({ ...createForm, firstName: e.target.value })} required /></div>
                <div><label>Last Name *</label><input className="input-full" value={createForm.lastName} onChange={e => setCreateForm({ ...createForm, lastName: e.target.value })} required /></div>
                <div><label>Username *</label><input className="input-full" value={createForm.username} onChange={e => setCreateForm({ ...createForm, username: e.target.value })} required /></div>
                <div><label>Employee ID *</label><input className="input-full" value={createForm.employeeId} onChange={e => setCreateForm({ ...createForm, employeeId: e.target.value })} required /></div>
                <div style={{ gridColumn: '1/-1' }}><label>Email</label><input className="input-full" type="email" value={createForm.email} onChange={e => setCreateForm({ ...createForm, email: e.target.value })} placeholder="Optional" /></div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label>Password *</label>
                  <input className="input-full" type="password" value={createForm.password} onChange={e => setCreateForm({ ...createForm, password: e.target.value })} required minLength={10} placeholder="Min 10 characters" />
                  <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 6, padding: '10px 14px', marginTop: 6, fontSize: '0.8rem', color: '#555', lineHeight: 1.7 }}>
                    Min 10 chars · 1 uppercase (A–Z) · 1 lowercase (a–z) · 1 number (0–9) · 1 special character (!@#$%^&amp;*…)
                  </div>
                </div>
                <div>
                  <label>System Role</label>
                  <select className="input-full" value={createForm.role} onChange={e => setCreateForm({ ...createForm, role: e.target.value })}>
                    {SYSTEM_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label>Department</label>
                  <select className="input-full" value={createForm.departmentId} onChange={e => setCreateForm({ ...createForm, departmentId: e.target.value })}>
                    <option value="">-- None --</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div>
                  <label>Country</label>
                  <select className="input-full" value={createForm.countryId} onChange={e => setCreateForm({ ...createForm, countryId: e.target.value })}>
                    <option value="">-- None --</option>
                    {countries.map(c => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
                  </select>
                </div>
              </div>
              {createError && <p style={{ color: '#dc2626', marginTop: 8 }}>{createError}</p>}
              <div style={{ position: 'sticky', bottom: 0, background: '#fff', paddingTop: 12, paddingBottom: 4, display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
                <button type="submit" className="btn-primary" style={{ flex: '1 1 0', minWidth: 0, width: 0 }} disabled={createLoading}>{createLoading ? 'Creating...' : 'Create User'}</button>
                <button type="button" className="btn-secondary" style={{ flex: '1 1 0', minWidth: 0, width: 0 }} onClick={() => setCreateModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* FULL EDIT MODAL */}
      {editModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <h2>Edit: {editModal.firstName} {editModal.lastName}</h2>
            <form onSubmit={handleEdit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div><label>First Name</label><input className="input-full" value={editForm.firstName} onChange={e => setEditForm({ ...editForm, firstName: e.target.value })} /></div>
                <div><label>Last Name</label><input className="input-full" value={editForm.lastName} onChange={e => setEditForm({ ...editForm, lastName: e.target.value })} /></div>
                <div><label>Username</label><input className="input-full" value={editForm.username} onChange={e => setEditForm({ ...editForm, username: e.target.value })} /></div>
                <div><label>Employee ID</label><input className="input-full" value={editForm.employeeId} onChange={e => setEditForm({ ...editForm, employeeId: e.target.value })} /></div>
                <div style={{ gridColumn: '1/-1' }}><label>Email</label><input className="input-full" type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} /></div>
                <div>
                  <label>System Role</label>
                  <select className="input-full" value={editForm.role} onChange={e => setEditForm({ ...editForm, role: e.target.value })}>
                    {SYSTEM_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label>Department</label>
                  <select className="input-full" value={editForm.departmentId} onChange={e => setEditForm({ ...editForm, departmentId: e.target.value })}>
                    <option value="">-- None --</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div>
                  <label>Country</label>
                  <select className="input-full" value={editForm.countryId} onChange={e => setEditForm({ ...editForm, countryId: e.target.value })}>
                    <option value="">-- None --</option>
                    {countries.map(c => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
                  </select>
                </div>
              </div>
              {editError && <p style={{ color: '#dc2626', marginTop: 8 }}>{editError}</p>}

              {/* PAGE PERMISSIONS */}
              {isSuperAdmin && (
                <div style={{ marginTop: 20, borderTop: '2px solid #e5e7eb', paddingTop: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <strong style={{ fontSize: 14, color: '#1e3a5f' }}>&#128274; Page Access Permissions</strong>
                    <span style={{ fontSize: 11, color: '#6b7280' }}>
                      {editPagePerms?.length === 0 ? '✅ Full access (all pages)' : `${editPagePerms?.length || '?'} page(s) allowed`}
                    </span>
                  </div>
                  <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 10px' }}>
                    Leave <em>all ticked</em> for full access. Untick to restrict the user to only selected pages.
                  </p>
                  {editPagePerms === null ? (
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>Loading…</div>
                  ) : (
                    <>
                      {['Core','HR','IT','Facility','Admin'].map(group => (
                        <div key={group} style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{group}</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {PAGE_LIST.filter(p => p.group === group).map(page => {
                              const checked = editPagePerms.length === 0 || editPagePerms.includes(page.key);
                              return (
                                <label key={page.key} style={{
                                  display: 'flex', alignItems: 'center', gap: 5, fontSize: 12,
                                  background: checked ? '#eff6ff' : '#f9fafb',
                                  border: `1px solid ${checked ? '#93c5fd' : '#e5e7eb'}`,
                                  borderRadius: 6, padding: '4px 8px', cursor: 'pointer'
                                }}>
                                  <input type="checkbox" checked={checked}
                                    onChange={e => {
                                      const allKeys = PAGE_LIST.map(p => p.key);
                                      // Expand from "all" (empty) to explicit list first
                                      const base = editPagePerms.length === 0 ? allKeys : [...editPagePerms];
                                      if (e.target.checked) {
                                        const next = base.includes(page.key) ? base : [...base, page.key];
                                        setEditPagePerms(next.length === allKeys.length ? [] : next);
                                      } else {
                                        setEditPagePerms(base.filter(k => k !== page.key));
                                      }
                                    }}
                                  />
                                  {page.label}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      {permsMsg && <p style={{ fontSize: 12, color: permsMsg.includes('✓') ? '#16a34a' : '#dc2626', marginTop: 4 }}>{permsMsg}</p>}
                      <button type="button"
                        disabled={savingPerms}
                        onClick={async () => {
                          setSavingPerms(true); setPermsMsg('');
                          try {
                            const res = await fetch(`${API_BASE}/admin/users/${editModal.id}/page-permissions`, {
                              method: 'PUT',
                              headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
                              body: JSON.stringify({ allowedPages: editPagePerms })
                            });
                            const d = await res.json();
                            if (res.ok) setPermsMsg('✓ Permissions saved.');
                            else setPermsMsg(d.message || 'Failed to save.');
                          } catch { setPermsMsg('Network error.'); }
                          finally { setSavingPerms(false); }
                        }}
                        style={{ marginTop: 8, fontSize: 12, padding: '5px 14px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                      >
                        {savingPerms ? 'Saving…' : 'Save Permissions'}
                      </button>
                    </>
                  )}
                </div>
              )}
              <div style={{ position: 'sticky', bottom: 0, background: '#fff', paddingTop: 12, paddingBottom: 4, display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
                <button type="submit" className="btn-primary" style={{ flex: '1 1 0', minWidth: 0, width: 0 }} disabled={editLoading}>{editLoading ? 'Saving...' : 'Save Changes'}</button>
                <button type="button" className="btn-secondary" style={{ flex: '1 1 0', minWidth: 0, width: 0 }} onClick={() => setEditModal(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* RESET PASSWORD MODAL */}
      {resetModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <h2>Reset Password</h2>
            <p style={{ color: '#6b7280', marginBottom: 12 }}>New password for <strong>{resetModal.firstName} {resetModal.lastName}</strong></p>
            <input className="input-full" type="password" placeholder="Enter new password" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
            <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 8, padding: '12px 16px', marginTop: 12, fontSize: '0.82rem', color: '#555', lineHeight: 1.7 }}>
              <strong style={{ display: 'block', marginBottom: 4, color: '#333' }}>Password requirements:</strong>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                <li>Minimum 10 characters</li>
                <li>At least 1 uppercase letter (A–Z)</li>
                <li>At least 1 lowercase letter (a–z)</li>
                <li>At least 1 number (0–9)</li>
                <li>At least 1 special character (!@#$%^&*…)</li>
              </ul>
            </div>
            {resetError && <p style={{ color: '#dc2626', marginTop: 8 }}>{resetError}</p>}
            <div style={{ position: 'sticky', bottom: 0, background: '#fff', paddingTop: 12, paddingBottom: 4, display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
              <button className="btn-primary" style={{ flex: '1 1 0', minWidth: 0, width: 0 }} onClick={handleResetPassword}>Reset Password</button>
              <button className="btn-secondary" style={{ flex: '1 1 0', minWidth: 0, width: 0 }} onClick={() => setResetModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
}

export default UserManagement;
